/**
 * CalendarWidget - AI-integrated calendar display
 *
 * Wave 6 of Sales AI Roadmap
 *
 * Features:
 * - Connect/disconnect Google Calendar
 * - Display today's events
 * - Show free time slots
 * - Mini week view
 * - Click to schedule
 */

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../../lib/apiClient';
import './CalendarWidget.css';

// Time formatting helper
function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Duration formatting helper
function formatDuration(minutes) {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

// Check if event is happening now
function isHappeningNow(start, end) {
  const now = new Date();
  return new Date(start) <= now && now <= new Date(end);
}

// Check if event is in the past
function isPast(end) {
  return new Date(end) < new Date();
}

/**
 * CalendarWidget - Main calendar display component
 */
export default function CalendarWidget({
  date,
  showConnect = true,
  showFreeSlots = true,
  compact = false,
  onAction,
}) {
  const [status, setStatus] = useState({ connected: false, loading: true });
  const [events, setEvents] = useState([]);
  const [freeSlots, setFreeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Check calendar connection status
  const checkStatus = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/calendar/status');
      setStatus({ ...data, loading: false });
      return data.connected;
    } catch (err) {
      console.error('Failed to check calendar status:', err);
      setStatus({ connected: false, loading: false });
      return false;
    }
  }, []);

  // Fetch events for the day
  const fetchEvents = useCallback(async (targetDate) => {
    try {
      setLoading(true);
      const start = new Date(targetDate || date || new Date());
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setHours(23, 59, 59, 999);

      const data = await apiClient.get(
        `/api/calendar/events?start=${start.toISOString()}&end=${end.toISOString()}`
      );

      setEvents(data.events || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch calendar events:', err);
      if (err.message?.includes('not connected')) {
        setStatus({ connected: false, loading: false });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [date]);

  // Fetch free slots
  const fetchFreeSlots = useCallback(async (targetDate) => {
    if (!showFreeSlots) return;

    try {
      const data = await apiClient.get(
        `/api/calendar/free-slots?date=${targetDate || date || new Date().toISOString().split('T')[0]}&duration=30`
      );
      setFreeSlots(data.free_slots || []);
    } catch (err) {
      console.error('Failed to fetch free slots:', err);
    }
  }, [date, showFreeSlots]);

  // Initial load
  useEffect(() => {
    async function init() {
      const connected = await checkStatus();
      if (connected) {
        await Promise.all([fetchEvents(), fetchFreeSlots()]);
      }
    }
    init();
  }, [checkStatus, fetchEvents, fetchFreeSlots]);

  // Handle connect calendar
  const handleConnect = async () => {
    try {
      const data = await apiClient.get('/api/calendar/auth');
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      console.error('Failed to start calendar auth:', err);
      setError('Failed to connect calendar');
    }
  };

  // Handle disconnect calendar
  const handleDisconnect = async () => {
    try {
      await apiClient.del('/api/calendar/disconnect');
      setStatus({ connected: false, loading: false });
      setEvents([]);
      setFreeSlots([]);
    } catch (err) {
      console.error('Failed to disconnect calendar:', err);
      setError('Failed to disconnect');
    }
  };

  // Handle sync
  const handleSync = async () => {
    try {
      setSyncing(true);
      await apiClient.post('/api/calendar/sync');
      await fetchEvents();
      await fetchFreeSlots();
    } catch (err) {
      console.error('Failed to sync calendar:', err);
      setError('Failed to sync');
    } finally {
      setSyncing(false);
    }
  };

  // Handle schedule action
  const handleSchedule = (slot) => {
    if (onAction) {
      onAction({
        type: 'schedule',
        slot,
        query: `Schedule something at ${formatTime(slot.start)}`,
      });
    }
  };

  // Handle event click
  const handleEventClick = (event) => {
    if (onAction) {
      onAction({
        type: 'view_event',
        event,
        query: `Tell me about my ${event.title} meeting`,
      });
    }
  };

  // Loading state
  if (status.loading) {
    return (
      <div className={`calendar-widget ${compact ? 'calendar-widget--compact' : ''}`}>
        <div className="calendar-widget__loading">
          <div className="calendar-widget__spinner" />
          <span>Loading calendar...</span>
        </div>
      </div>
    );
  }

  // Not connected state
  if (!status.connected && showConnect) {
    return (
      <div className={`calendar-widget ${compact ? 'calendar-widget--compact' : ''}`}>
        <div className="calendar-widget__connect">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <h3>Connect Your Calendar</h3>
          <p>Let the AI see your schedule to find free time and schedule tasks around meetings.</p>
          <button className="calendar-widget__connect-btn" onClick={handleConnect}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Connect Google Calendar
          </button>
        </div>
      </div>
    );
  }

  // Compact view
  if (compact) {
    return (
      <div className="calendar-widget calendar-widget--compact">
        <div className="calendar-widget__mini-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span>{events.length} events today</span>
          {freeSlots.length > 0 && (
            <span className="calendar-widget__free-badge">
              {freeSlots.length} free slots
            </span>
          )}
        </div>
        {events.slice(0, 3).map((event, idx) => (
          <div
            key={event.id || idx}
            className={`calendar-widget__mini-event ${isHappeningNow(event.start_time, event.end_time) ? 'calendar-widget__mini-event--now' : ''}`}
            onClick={() => handleEventClick(event)}
          >
            <span className="calendar-widget__mini-time">{formatTime(event.start_time)}</span>
            <span className="calendar-widget__mini-title">{event.title}</span>
          </div>
        ))}
      </div>
    );
  }

  // Full view
  const currentEvent = events.find((e) => isHappeningNow(e.start_time, e.end_time));
  const upcomingEvents = events.filter((e) => !isPast(e.end_time) && !isHappeningNow(e.start_time, e.end_time));
  const pastEvents = events.filter((e) => isPast(e.end_time));

  return (
    <div className="calendar-widget">
      {/* Header */}
      <div className="calendar-widget__header">
        <div className="calendar-widget__header-left">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <h3>Today's Schedule</h3>
        </div>
        <div className="calendar-widget__header-actions">
          <button
            className="calendar-widget__icon-btn"
            onClick={handleSync}
            disabled={syncing}
            title="Sync calendar"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={syncing ? 'spinning' : ''}
            >
              <path d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
          </button>
          <button
            className="calendar-widget__icon-btn calendar-widget__icon-btn--danger"
            onClick={handleDisconnect}
            title="Disconnect calendar"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="calendar-widget__error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Loading events */}
      {loading && (
        <div className="calendar-widget__loading">
          <div className="calendar-widget__spinner" />
          <span>Loading events...</span>
        </div>
      )}

      {/* No events */}
      {!loading && events.length === 0 && (
        <div className="calendar-widget__empty">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
          <span>No events scheduled for today</span>
          <span className="calendar-widget__empty-sub">Your calendar is clear!</span>
        </div>
      )}

      {/* Current event */}
      {currentEvent && (
        <div className="calendar-widget__current">
          <div className="calendar-widget__current-badge">
            <span className="calendar-widget__pulse" />
            Happening Now
          </div>
          <div
            className="calendar-widget__event calendar-widget__event--current"
            onClick={() => handleEventClick(currentEvent)}
          >
            <div className="calendar-widget__event-time">
              {formatTime(currentEvent.start_time)} - {formatTime(currentEvent.end_time)}
            </div>
            <div className="calendar-widget__event-title">{currentEvent.title}</div>
            {currentEvent.location && (
              <div className="calendar-widget__event-location">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {currentEvent.location}
              </div>
            )}
            {currentEvent.hangoutLink && (
              <a
                href={currentEvent.hangoutLink}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-widget__event-link"
                onClick={(e) => e.stopPropagation()}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 10l5 5-5 5" />
                  <path d="M4 4v7a4 4 0 004 4h12" />
                </svg>
                Join meeting
              </a>
            )}
          </div>
        </div>
      )}

      {/* Upcoming events */}
      {upcomingEvents.length > 0 && (
        <div className="calendar-widget__section">
          <div className="calendar-widget__section-title">Upcoming</div>
          {upcomingEvents.map((event, idx) => (
            <div
              key={event.id || idx}
              className="calendar-widget__event"
              onClick={() => handleEventClick(event)}
            >
              <div className="calendar-widget__event-time">
                {formatTime(event.start_time)} - {formatTime(event.end_time)}
              </div>
              <div className="calendar-widget__event-title">{event.title}</div>
              {event.attendees?.length > 0 && (
                <div className="calendar-widget__event-attendees">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                  </svg>
                  {event.attendees.length} attendees
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Free slots */}
      {showFreeSlots && freeSlots.length > 0 && (
        <div className="calendar-widget__section">
          <div className="calendar-widget__section-title">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            Free Time Slots
          </div>
          <div className="calendar-widget__free-slots">
            {freeSlots.slice(0, 4).map((slot, idx) => (
              <button
                key={idx}
                className="calendar-widget__free-slot"
                onClick={() => handleSchedule(slot)}
              >
                <span className="calendar-widget__free-slot-time">
                  {formatTime(slot.start)}
                </span>
                <span className="calendar-widget__free-slot-duration">
                  {formatDuration(slot.duration_minutes)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Past events (collapsed) */}
      {pastEvents.length > 0 && (
        <div className="calendar-widget__section calendar-widget__section--past">
          <div className="calendar-widget__section-title">
            Earlier Today ({pastEvents.length})
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * MiniCalendar - Week view mini calendar
 */
export function MiniCalendar({ selectedDate, onDateSelect, events = [] }) {
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  });

  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    days.push(date);
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().toDateString();

  // Count events per day
  const eventCounts = {};
  events.forEach((event) => {
    const dateStr = new Date(event.start_time).toDateString();
    eventCounts[dateStr] = (eventCounts[dateStr] || 0) + 1;
  });

  return (
    <div className="mini-calendar">
      <div className="mini-calendar__header">
        <button onClick={() => {
          const newStart = new Date(weekStart);
          newStart.setDate(newStart.getDate() - 7);
          setWeekStart(newStart);
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span>
          {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
          {days[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
        <button onClick={() => {
          const newStart = new Date(weekStart);
          newStart.setDate(newStart.getDate() + 7);
          setWeekStart(newStart);
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
      <div className="mini-calendar__days">
        {days.map((date, idx) => {
          const dateStr = date.toDateString();
          const isToday = dateStr === today;
          const isSelected = selectedDate && new Date(selectedDate).toDateString() === dateStr;
          const eventCount = eventCounts[dateStr] || 0;

          return (
            <button
              key={idx}
              className={`mini-calendar__day ${isToday ? 'mini-calendar__day--today' : ''} ${isSelected ? 'mini-calendar__day--selected' : ''}`}
              onClick={() => onDateSelect?.(date.toISOString().split('T')[0])}
            >
              <span className="mini-calendar__day-name">{dayNames[idx]}</span>
              <span className="mini-calendar__day-number">{date.getDate()}</span>
              {eventCount > 0 && (
                <span className="mini-calendar__day-dots">
                  {Array(Math.min(eventCount, 3)).fill(0).map((_, i) => (
                    <span key={i} className="mini-calendar__day-dot" />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * CalendarEventCard - Single event display
 */
export function CalendarEventCard({ event, compact = false, onAction }) {
  if (!event) return null;

  const isNow = isHappeningNow(event.start_time, event.end_time);
  const past = isPast(event.end_time);

  return (
    <div
      className={`calendar-event-card ${isNow ? 'calendar-event-card--now' : ''} ${past ? 'calendar-event-card--past' : ''} ${compact ? 'calendar-event-card--compact' : ''}`}
      onClick={() => onAction?.({ type: 'view_event', event })}
    >
      {isNow && (
        <div className="calendar-event-card__now-badge">
          <span className="calendar-event-card__pulse" />
          Now
        </div>
      )}
      <div className="calendar-event-card__time">
        {formatTime(event.start_time)} - {formatTime(event.end_time)}
      </div>
      <div className="calendar-event-card__title">{event.title}</div>
      {!compact && event.location && (
        <div className="calendar-event-card__location">{event.location}</div>
      )}
      {event.hangoutLink && (
        <a
          href={event.hangoutLink}
          target="_blank"
          rel="noopener noreferrer"
          className="calendar-event-card__join"
          onClick={(e) => e.stopPropagation()}
        >
          Join
        </a>
      )}
    </div>
  );
}
